/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useParams } from 'js/lib/useRouter';

import { Grid } from '@coursera/cds-core';

import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import EnterpriseSearchBarLegacyGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseSearchBarLegacyGridItem';
import ExploreMegaMenuGridItem from 'bundles/composable-page-header-enterprise/components/ExploreMegaMenuGridItem';
import type { UseEnterpriseMyPageHeaderResult as MyEnterprisePageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import {
  getEnterpriseLogoLabel,
  getEnterpriseProgramLogos,
  getLinkForEnterpriseProgram,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import LeftNavButtonGridItem from 'bundles/composable-page-header/components/grid-items/LeftNavButtonGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import ProgramSwitcherGridItem from 'bundles/composable-page-header/components/grid-items/ProgramSwitcherGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import useShouldShowHelpDropdown from 'bundles/composable-page-header/hooks/useShouldShowHelpDropdown';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import { isOnMyLearningPage } from 'bundles/composable-page-header/utils/headerHighlightLinkUtils';
import useUnifiedEnterpriseSearch from 'bundles/search-common/hooks/useUnifiedEnterpriseSearch';

import _t from 'i18n!nls/composable-page-header-enterprise';

const styles = {
  leftSide: css`
    width: auto;
  `,
  middleSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    padding: 0 8px;
    width: 100%;
    min-width: 0;
  `,
  rightSide: css`
    flex-shrink: 1;
    flex-grow: 1;
    flex-basis: 0%;
    width: auto;
  `,
  exploreItem: css`
    display: flex;
    flex-direction: row;
  `,
};

type Props = {
  pageHeaderData: MyEnterprisePageHeader;
  hideSearch?: boolean;
  hideExplore?: boolean;
  injectedSearch?: React.ReactNode;
};

const LoggedInEnterpriseDesktopMainNav = ({ pageHeaderData, hideSearch, hideExplore, injectedSearch }: Props) => {
  const {
    currentPageProgram,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
  } = pageHeaderData;
  const location = useLocation();
  const params = useParams();
  const currentProgram = currentPageProgram ?? chosenEnterpriseProgram;
  const showUnifiedSearch = useUnifiedEnterpriseSearch();
  const shouldShowHelpDropdown = useShouldShowHelpDropdown();
  const myLearningUrl =
    location.pathname.includes('/programs/all') && params.orgSlug
      ? `/programs/all/${params.orgSlug}?currentTab=MY_LEARNING`
      : `/programs/${currentProgram?.slug}/my-learning?myLearningTab=IN_PROGRESS`;

  const searchComponent =
    injectedSearch ??
    (showUnifiedSearch ? (
      <EnterpriseOrgScopedSearchBarGridItem
        thirdPartyOrganizationSlug={currentProgram?.naptimeThirdPartyOrganization?.slug}
        enrolledEnterprisePrograms={enrolledEnterprisePrograms}
        chosenProgramSlug={currentProgram?.slug}
        chosenProgramId={currentProgram?.id}
      />
    ) : (
      <EnterpriseSearchBarLegacyGridItem
        currentProgram={currentProgram}
        enterprisePrograms={enrolledEnterprisePrograms}
      />
    ));

  return (
    <MainNavigationWrapper>
      <Grid item container alignItems="center" spacing={8} css={styles.leftSide}>
        {hasEnrolledPrograms && (
          <Grid item>
            <ProgramSwitcherGridItem
              currentProgram={currentProgram}
              programs={enrolledEnterprisePrograms}
              degrees={enrolledDegreePrograms}
            />
          </Grid>
        )}
        <Grid item>
          <HomeLogoGridItemModule
            href={currentProgram ? getLinkForEnterpriseProgram(currentProgram) : '/'}
            logos={currentProgram ? getEnterpriseProgramLogos(currentProgram) : [<CourseraHomeLogo key="1" />]}
            ariaLabel={currentProgram ? getEnterpriseLogoLabel(currentProgram) : undefined}
          />
        </Grid>
        {!hideExplore && (
          <Grid item css={styles.exploreItem}>
            <ExploreMegaMenuGridItem />
          </Grid>
        )}
        {currentProgram &&
          enrolledEnterprisePrograms?.some(
            (program) => program.naptimeThirdPartyOrganization.id === currentProgram.naptimeThirdPartyOrganization.id
          ) && (
            <Grid item>
              <LeftNavButtonGridItem
                label={_t('My Learning')}
                href={myLearningUrl}
                name="my_learning"
                trackingName="lihp_my_learning"
                isCurrent={isOnMyLearningPage(location.pathname)}
              />
            </Grid>
          )}
      </Grid>
      <Grid item container alignItems="center" css={styles.middleSide}>
        {!hideSearch && searchComponent}
      </Grid>
      <Grid item container alignItems="center" justifyContent="flex-end" wrap="nowrap" css={styles.rightSide}>
        {shouldShowHelpDropdown && (
          <Grid item>
            <HelpDropdownGridItem disableChatSsr={true} />
          </Grid>
        )}
        <Grid item>
          <LanguageDropdownLegacyGridItem currentThirdPartyOrgId={currentProgram?.naptimeThirdPartyOrganization?.id} />
        </Grid>
        <Grid item>
          <NotificationsLegacyGridItem />
        </Grid>
        {showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        <Grid item>
          <DefaultAccountDropdownGridItemRenderer
            hasEnrolledPrograms={!!hasEnrolledPrograms}
            naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
            hasCheatingIncidents={!!hasCheatingIncidents}
            canViewEducatorAdminLink={!!canViewEducatorAdminLink}
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
            currentProgramSlug={currentProgram?.slug}
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInEnterpriseDesktopMainNav;
